// Stopped (history) wind session card — mirrors legacy renderWindClosedCard.
import { useNavigate } from "react-router-dom";
import type { WindSessionWithLatest } from "@/types/api";
import { fmt } from "@/lib/format";
import TimeAgo from "@/components/common/time-ago";

interface WindStoppedCardProps {
  row: WindSessionWithLatest;
}

export default function WindStoppedCard({ row }: WindStoppedCardProps) {
  const { session, latest_cycle, cycles_count, total_pnl } = row;
  const navigate = useNavigate();

  const total = total_pnl ?? 0;
  const pnlColor = total >= 0 ? "#22c55e" : "#ef4444";
  const dir =
    latest_cycle?.direction ?? session.direction_initial ?? "long";
  const dirHex = dir === "long" ? "#22c55e" : "#ef4444";
  const lastTs = session.stopped_at ?? session.created_at;

  return (
    <div
      className="bg-surface border border-border rounded-lg p-3 cursor-pointer hover:border-accent/40 transition-colors opacity-75"
      onClick={() => navigate(`/wind/${session.id}`)}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="flex items-baseline gap-1.5 min-w-0">
          <span className="font-semibold text-sm text-text truncate">
            {session.symbol}
          </span>
          <span className="text-[0.72rem] shrink-0">
            <span className="font-semibold" style={{ color: pnlColor }}>
              {total >= 0 ? "+" : ""}
              {fmt(total, 4)}
            </span>
            <span className="text-muted">
              {" · "}
              {cycles_count} cycle{cycles_count === 1 ? "" : "s"}
            </span>
          </span>
        </span>

        <div className="flex flex-wrap gap-1 shrink-0">
          <span
            className="inline-flex items-center text-[0.7rem] font-medium px-1.5 py-0.5 rounded"
            style={{ background: `${dirHex}20`, color: dirHex }}
          >
            {dir === "long" ? "LONG" : "SHORT"}
          </span>
          <span className="text-[0.7rem] px-1.5 py-0.5 rounded bg-muted/20 text-muted">
            stopped
          </span>
        </div>
      </div>

      {/* Meta row */}
      <div className="text-[0.72rem] text-muted flex flex-wrap gap-x-1.5">
        <span className="text-success">TP {session.tp_pct}%</span>
        <span>/</span>
        <span className="text-danger">SL {session.sl_pct}%</span>
        <span>·</span>
        <span className="font-semibold" style={{ color: "#a855f7" }}>
          {session.leverage}x
        </span>
        <span>·</span>
        <span>last <TimeAgo iso={lastTs} /></span>
      </div>
    </div>
  );
}
